import callApi from "helpers/callApi";
import { buildFundRequestSupportingFilesFormData } from "../tabs/requestFunds/model/requestFundsSupportingFiles";

export const FUND_REQUEST_CREATE_ENDPOINT = "/manager/billing_history/fund_request";
export const FUND_MANAGERS_ENDPOINT = "/manager/billing_history/fund_managers";
export const FUND_REQUEST_NEXT_NUMBER_ENDPOINT = `${FUND_REQUEST_CREATE_ENDPOINT}/next_number`;

export function getFundRequestByIdEndpoint(fundRequestId) {
  return `/manager/billing_history/fund_request/${fundRequestId}`;
}

export function getFundRequestSupportingFilesEndpoint(fundRequestId) {
  return `${getFundRequestByIdEndpoint(fundRequestId)}/supporting_files`;
}

export function getFundRequestSupportingFileDeleteEndpoint(fundRequestId, fileId) {
  return `${getFundRequestSupportingFilesEndpoint(fundRequestId)}/${fileId}`;
}

export const getFundRequestUpdateEndpoint = getFundRequestByIdEndpoint;
export const getFundRequestDeleteEndpoint = getFundRequestByIdEndpoint;

export class FundRequestApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = "FundRequestApiError";
    this.status = status;
    this.body = body;
  }
}

async function readFundRequestResponseBody(response) {
  try {
    const text = await response.text();
    if (text == null || String(text).trim() === "") {
      return null;
    }
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function throwFundRequestApiError(response) {
  const body = await readFundRequestResponseBody(response);
  throw new FundRequestApiError(response.errorMessage || "Error", {
    status: response.status,
    body,
  });
}

export async function fetchFundManagers(search) {
  const params = {};
  const query = String(search ?? "").trim();
  if (query) {
    params.search = query;
  }
  const response = await callApi(FUND_MANAGERS_ENDPOINT, "GET", params);
  if (!response.ok) {
    await throwFundRequestApiError(response);
  }
  return response.json();
}

export async function fetchNextRequestNumber() {
  const response = await callApi(FUND_REQUEST_NEXT_NUMBER_ENDPOINT, "GET");
  if (!response.ok) {
    await throwFundRequestApiError(response);
  }
  return response.json();
}

export async function createFundRequest(payload) {
  const response = await callApi(FUND_REQUEST_CREATE_ENDPOINT, "POST", payload);
  if (!response.ok) {
    await throwFundRequestApiError(response);
  }
  return response.json();
}

export async function updateFundRequest(fundRequestId, payload) {
  const response = await callApi(getFundRequestUpdateEndpoint(fundRequestId), "PUT", payload);
  if (!response.ok) {
    await throwFundRequestApiError(response);
  }
  return response.json();
}

export async function fetchFundRequestManagerDetail(fundRequestId) {
  const response = await callApi(getFundRequestByIdEndpoint(fundRequestId), "GET");
  if (!response.ok) {
    await throwFundRequestApiError(response);
  }
  return response.json();
}

export async function deleteFundRequest(fundRequestId) {
  const response = await callApi(getFundRequestDeleteEndpoint(fundRequestId), "DELETE");
  if (!response.ok) {
    await throwFundRequestApiError(response);
  }
  if (response.status === 204) {
    return null;
  }
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

export async function uploadFundRequestSupportingFiles(fundRequestId, files) {
  const formData = buildFundRequestSupportingFilesFormData(files);
  const response = await callApi(
    getFundRequestSupportingFilesEndpoint(fundRequestId),
    "POST",
    formData,
    { contentType: "multipart/form-data" },
  );
  if (!response.ok) {
    await throwFundRequestApiError(response);
  }
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function deleteFundRequestSupportingFile(fundRequestId, fileId) {
  const response = await callApi(
    getFundRequestSupportingFileDeleteEndpoint(fundRequestId, fileId),
    "DELETE",
    {},
    { omitBody: true },
  );
  if (!response.ok) {
    await throwFundRequestApiError(response);
  }
  if (response.status === 204) {
    return null;
  }
  try {
    return await response.json();
  } catch {
    return null;
  }
}
